/**
 * adminNav.ts — THE Administration-area nav model (Story UI-U6 AC1).
 *
 * One config module consumed by AdminSidebar + the route components + tests.
 * Groups + German titles/descriptions are ported from the reference prototype's
 * `admin-sidebar.tsx` (ADMIN_NAV + ADMIN_PAGE_META), with two deliberate deltas:
 *   - `tarifmatrix` is DROPPED — sibling story UI-U5 owns `/admin/tarifmatrix`
 *     and slots its own entry into the Mandant group when its page lands
 *     (one-entry-per-line structure keeps that a clean, additive merge).
 *   - copy softened for our honest omissions (no 2FA "Sicherheitsstatus", etc.).
 *
 * Each page carries `requiredKeys`: the caller sees the nav entry (and the page
 * renders) only when they hold ANY of these EXISTING Epic-12 keys. Empty list =
 * ungated (System-Status = public GET /health). The api guards remain the hard
 * line — this list is purely guard-AWARENESS rendered from the server's answer.
 *
 * [Source: brokly-design-reference/components/brokly/admin/admin-sidebar.tsx]
 * [Source: docs/stories/ui-u6-admin-section.md AC1 + Endpoint inventory]
 */

import type { ComponentType } from 'react';
import {
  Users,
  ShieldCheck,
  KeyRound,
  UsersRound,
  ShieldQuestion,
  Building2,
  ReceiptText,
  Workflow,
  Activity,
  ScrollText,
  Mailbox,
  MailX,
  Ban,
  PenLine,
  Signature,
  ShieldAlert,
  BellRing,
  BookOpenCheck,
} from 'lucide-react';
import type { EffectivePermissions } from './useEffectivePermissions';

/** The route param values under `/admin/$section` (one per page). */
export type AdminPageKey =
  | 'mitglieder'
  | 'rollen'
  | 'berechtigungsgruppen'
  | 'teams'
  | 'kundenteams'
  | 'mandant'
  | 'rechnungspruefung'
  | 'regelwerk'
  | 'workflows'
  | 'prozess-erinnerungen'
  | 'unterschriften'
  | 'mahnwesen'
  | 'versicherer-fristen'
  | 'brokermandat-meldung'
  | 'mandatsaufloesung'
  | 'system-status'
  | 'kontrolle'
  | 'audit-log'
  | 'postfaecher'
  | 'blockliste'
  | 'ausschlussliste'
  | 'signatur'
  | 'zeichnungsberechtigte';

export interface AdminNavItem {
  key: AdminPageKey;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** ANY of these keys grants the entry. Empty = ungated (System-Status). */
  requiredKeys: readonly string[];
}

export interface AdminNavGroup {
  label: string;
  items: AdminNavItem[];
}

/** The grouped Administration nav (order matters — first-visible-page lands here). */
export const ADMIN_NAV: AdminNavGroup[] = [
  {
    label: 'Organisation',
    items: [
      { key: 'mitglieder', label: 'Mitglieder', icon: Users, requiredKeys: ['role.read'] },
      { key: 'rollen', label: 'Rollen & Rechte', icon: ShieldCheck, requiredKeys: ['role.read'] },
      {
        key: 'berechtigungsgruppen',
        label: 'Berechtigungsgruppen',
        icon: KeyRound,
        requiredKeys: ['role.read'],
      },
      { key: 'teams', label: 'Teams', icon: UsersRound, requiredKeys: ['funktionsgruppe.read'] },
      {
        key: 'kundenteams',
        label: 'Kundenteams',
        icon: ShieldQuestion,
        requiredKeys: ['admin.team.read'],
      },
      {
        key: 'zeichnungsberechtigte',
        label: 'Zeichnungsberechtigte',
        icon: Signature,
        // `signing.read` is an ORIGINAL BASIC_BASELINE_KEYS entry, so every already-seeded
        // role holds it (§3.3 item 6: a new key would reach nobody and the page would ship
        // dead). Writing is gated on `tenant.manage` by the api, not by this nav entry.
        requiredKeys: ['signing.read'],
      },
    ],
  },
  {
    label: 'Mandant',
    items: [
      {
        key: 'mandant',
        label: 'Mandant & Branding',
        icon: Building2,
        // Nav-gated on the ADMIN tab keys (NOT policy.read — the AVB tab's key is
        // in every broker's baseline; the page itself is an admin surface).
        requiredKeys: ['tenant.read', 'portal_config.manage', 'portal_grant.read'],
      },
      {
        key: 'rechnungspruefung',
        label: 'Rechnungskontrolle',
        icon: ReceiptText,
        requiredKeys: ['tolerance_config.manage'],
      },
      {
        // P2 Placement Slice 3 — the per-tenant Regelwerk (Soll-Deckungskatalog + Attribut-Schema).
        // Nav-gated on regelwerk.read (a NEW admin key, NOT in the baseline); the api gates the
        // writes on regelwerk.manage.
        key: 'regelwerk',
        label: 'Regelwerk',
        icon: BookOpenCheck,
        requiredKeys: ['regelwerk.read'],
      },
    ],
  },
  {
    label: 'Automatisierung',
    items: [
      {
        key: 'workflows',
        label: 'Workflows',
        icon: Workflow,
        requiredKeys: ['channel_config.read', 'routing_policy.read'],
      },
      {
        // Berechtigungskonzept V3.0 / Einfuehrungskonzept · P7 — the ONE global reminder-frequency
        // setting (3 levels 5/10/20 days) for ALL processes. Admin config surface (tenant.read to
        // view, tenant.manage to edit — the api is the hard line).
        key: 'prozess-erinnerungen',
        label: 'Prozess-Erinnerungen',
        icon: BellRing,
        requiredKeys: ['tenant.read', 'tenant.manage'],
      },
      {
        // P4-S5 (Kap. 7.10) - the Unterschriften-Ähnlichkeitsschwellen. REUSES the confidence_policy
        // ['mandate_signing'] row, so the nav + save gate on the SAME keys the confidence-policy api
        // enforces (GET routing_policy.read, PUT routing_policy.manage) - like the Workflows screen.
        key: 'unterschriften',
        label: 'Dokumente & Unterschriften',
        icon: Signature,
        requiredKeys: ['routing_policy.read', 'routing_policy.manage'],
      },
      {
        // P4-S2 — the Analysemandat Mahnwesen parameters (Fristen, Eskalation, Versandfenster) +
        // the P-04 Kommunikationsbausteine. tenant.read to view, tenant.manage to edit (api = hard line).
        key: 'mahnwesen',
        label: 'Mahnwesen',
        icon: BellRing,
        requiredKeys: ['tenant.read', 'tenant.manage'],
      },
      {
        // P4-S7 - the Fristen for the insurers' answer to a Versicherer-Benachrichtigung (Frist 1,
        // Frist 2, Erinnerungen per prozess). The api gates GET and PUT on mahnwesen_config.manage,
        // so the nav does too.
        key: 'versicherer-fristen',
        label: 'Versicherer-Fristen',
        icon: BellRing,
        requiredKeys: ['mahnwesen_config.manage'],
      },
      {
        // The Brokermandat Meldung settings (Meldemodus, Eskalationsziel, Mandatsumfang switch). The api
        // gates GET on tenant.read and PUT on tenant.manage, so the nav does too.
        key: 'brokermandat-meldung',
        label: 'Brokermandat-Meldung',
        icon: BellRing,
        requiredKeys: ['tenant.read', 'tenant.manage'],
      },
      {
        // P6 - the rules of the Mandatsauflösung (Fristregel, Rückdatierung, Übergabe, Portal, Aufbewahrung).
        // The api gates GET on tenant.read and PUT on tenant.manage, so the nav does too.
        key: 'mandatsaufloesung',
        label: 'Mandatsauflösung',
        icon: BellRing,
        requiredKeys: ['tenant.read', 'tenant.manage'],
      },
      {
        key: 'ausschlussliste',
        label: 'Ausschlussliste',
        icon: Ban,
        requiredKeys: ['suppression.manage'],
      },
    ],
  },
  {
    label: 'System',
    items: [
      { key: 'system-status', label: 'System-Status', icon: Activity, requiredKeys: [] },
      // Berechtigungskonzept V4.0 · Bereich 5 Kontrollfunktion. This admin surface is the per-broker
      // FRISTEN CONFIG only — the cross-customer Rückstand LIST moved to the Portfolio dashboard
      // (gated on ...rueckstand.einsehen there). The config change "erfordert eine Administrationsrolle"
      // (Cockpit Entwurf Kap 7 = a Parametrierungsfunktion), so the nav is gated on the SAME admin keys
      // the api enforces (GET tenant.read, PUT tenant.manage) — NOT the einsehen control permission.
      {
        key: 'kontrolle',
        label: 'Kontrollfunktion',
        icon: ShieldAlert,
        requiredKeys: ['tenant.read', 'tenant.manage'],
      },
      { key: 'audit-log', label: 'Audit-Log', icon: ScrollText, requiredKeys: ['audit.read'] },
      {
        key: 'signatur',
        label: 'E-Mail-Signatur',
        icon: PenLine,
        // UNGATED, matching the api: this is a user editing their OWN signature, the same
        // category as their own profile. There is no id on the wire and the row comes from
        // the token, so the only signature a caller can reach is their own. Gating it on an
        // admin key would stop the employees B3 exists for from maintaining theirs.
        //
        // Placed AFTER system-status deliberately: `firstVisibleSection` returns the first
        // entry a caller may see, so putting an ungated page earlier would silently change
        // where a keyless user lands. [u6-n2b] pins that, and caught exactly this.
        requiredKeys: [],
      },
    ],
  },
  {
    // Story MC6 — the broker's OWN mailbox is a PERSONAL action → requiredKeys: []
    // (like System-Status, excluded from ADMIN_ALL_KEYS so it never surfaces the
    // app-shell "Administration" entry on its own). Placed LAST so it never
    // hijacks the /admin index redirect (firstVisibleSection keeps System-Status
    // as the first ungated section — see adminNav.test.ts [u6-n2]).
    label: 'Kommunikation',
    items: [
      { key: 'postfaecher', label: 'Postfächer', icon: Mailbox, requiredKeys: [] },
      // Berechtigungskonzept V3.0 · Bereich 1 (3.5) — the 3-tier blocklist. Personal +
      // tenant view for every broker (they manage their OWN user rules; tenant rules are
      // admin-managed, shown read-only otherwise). requiredKeys: [] like Postfächer — the
      // api still enforces crm.read + tenant.manage per operation; excluded from
      // ADMIN_ALL_KEYS so it never surfaces the app-shell Administration entry on its own.
      { key: 'blockliste', label: 'E-Mail-Blockliste', icon: MailX, requiredKeys: [] },
    ],
  },
];

/** Flat key → header metadata (title/description) for the content header. */
export const ADMIN_PAGE_META: Record<AdminPageKey, { title: string; description: string }> = {
  zeichnungsberechtigte: {
    title: 'Zeichnungsberechtigte Personen',
    description:
      'Personen, die Verträge unterzeichnen dürfen. Ein Benutzerkonto ist dafür nicht nötig.',
  },
  signatur: {
    title: 'E-Mail-Signatur',
    description: 'Ihre persönliche Signatur für ausgehende E-Mails.',
  },
  mitglieder: {
    title: 'Mitglieder',
    description: 'Benutzerkonten und Rollen Ihres Mandanten.',
  },
  rollen: {
    title: 'Rollen & Rechte',
    description: 'Standardrollen und ihre Berechtigungen definieren.',
  },
  berechtigungsgruppen: {
    title: 'Berechtigungsgruppen',
    description: 'Zusätzliche Rechte additiv zur Rolle vergeben.',
  },
  teams: {
    title: 'Teams',
    description: 'Funktionsgruppen für das Aufgaben-Routing.',
  },
  kundenteams: {
    title: 'Kundenteams',
    description:
      'Team-basierte Kundensichtbarkeit: Kunden einem Team zuweisen, damit sie nur für dessen Mitglieder sichtbar sind. Standard: alle sehen alle ausser die eigene Organisation.',
  },
  mandant: {
    title: 'Mandant & Branding',
    description: 'Mandantendaten, Kundenportal und AVB-Bibliothek.',
  },
  rechnungspruefung: {
    title: 'Rechnungskontrolle',
    description:
      'Kategorien K0 bis K3 für die automatische Rechnungskontrolle: Rundungstoleranz, t1 und t2. Der Fokus liegt auf Unterzahlung, denn zu wenig erhaltene Courtage ist das Risiko.',
  },
  regelwerk: {
    title: 'Regelwerk',
    description:
      'Soll-Deckungskatalog und Attribut-Schema Ihres Mandanten. Die Vorgaben stammen aus dem Brokly-Standard; Sie passen Regeln und Felder fuer Ihre Haussicht an, ohne dass neu ausgeliefert werden muss.',
  },
  workflows: {
    title: 'Workflows',
    description: 'Kanalsteuerung und automatisches Routing.',
  },
  'prozess-erinnerungen': {
    title: 'Prozess-Erinnerungen',
    description:
      'Pro Prozesstyp drei Erinnerungsstufen: Stufe 1 und 2 als Erinnerung per E-Mail oder ins Portal, Stufe 3 erstellt automatisch eine Aufgabe beim Kundenberater. Die Fristen sind pro Prozess einstellbar. Dazu ein dynamisches Vorlage-Template fuer alle Erinnerungen.',
  },
  unterschriften: {
    title: 'Dokumente & Unterschriften',
    description:
      'Ähnlichkeitssuche von Unterschriften: der Schwellwert für die automatische Annahme und die Untergrenze für «unsicher». Score ab dem Schwellwert gilt als bestanden, darunter bis zur Untergrenze als unsicher, darunter als Befund. Standardwerte dienen als Startpunkt; Änderungen werden im Audit-Log festgehalten.',
  },
  mahnwesen: {
    title: 'Mahnwesen',
    description:
      'Das Mahnwesen fuer das Analysemandat (P-04): Anzahl Erinnerungen und Fristen, die Eskalationsfrist und die Team-Lead-Frist, das Versandfenster sowie die Erinnerungstexte (S9/S10/S11). Jede Frist ist kundenanpassbar, Standardwerte dienen als Startpunkt.',
  },
  'versicherer-fristen': {
    title: 'Versicherer-Fristen',
    description:
      'Wie lange die Versicherer Zeit haben, auf eine Versicherer-Benachrichtigung zu antworten: Frist 1 bis zur Erinnerung, Frist 2 danach und die Anzahl Erinnerungen, je Prozess in Arbeitstagen. Die Standardwerte dienen als Startpunkt; jede Änderung wird im Audit-Log festgehalten.',
  },
  'brokermandat-meldung': {
    title: 'Brokermandat-Meldung',
    description:
      'Wie das unterzeichnete Brokermandat den Versicherern gemeldet wird: automatisch nach dem Abschluss oder als Vorschlag mit Freigabe durch den Broker, wer die Eskalation der Eintragungskontrolle erhält und ob ein unveränderter Rücklauf den Mandatsumfang ohne Prüfung bestätigt. Jede Änderung wird im Audit-Log festgehalten.',
  },
  mandatsaufloesung: {
    title: 'Mandatsauflösung',
    description:
      'Wie ein Mandat aufgelöst wird: das Enddatum einer Kündigung ohne Datum (Fristregel), ab wann ein Enddatum in der Vergangenheit eine Rückdatierung ist, wann die Übergabe vorbereitet wird, wie lange das Portal offen bleibt und wie lange die Unterlagen aufbewahrt werden. Jede Änderung wird im Audit-Log festgehalten.',
  },
  'system-status': {
    title: 'System-Status',
    description: 'Verfügbarkeit der Brokly-Dienste.',
  },
  kontrolle: {
    title: 'Kontrollfunktion',
    description:
      'Fristen für den Kontrollbereich pro Stufe (Beobachtung, Nachfassen, Kritisch, Eskalation). Die Rückstandsliste selbst liegt im Portfolio-Dashboard. Änderungen erfordern eine Administrationsrolle und wirken nicht rückwirkend.',
  },
  'audit-log': {
    title: 'Audit-Log',
    description: 'Unveränderbares Protokoll aller sicherheitsrelevanten Ereignisse.',
  },
  postfaecher: {
    title: 'Postfächer',
    description: 'Verbinde dein persönliches E-Mail-Postfach für die Kommunikation.',
  },
  blockliste: {
    title: 'E-Mail-Blockliste',
    description:
      'Absender, die nie oder nur eingeschränkt synchronisiert werden. Plattformregeln sind systemverwaltet, Mandantenregeln adminverwaltet; deine eigenen Regeln kannst du selbst verwalten (nur verschärfen, nie lockern).',
  },
  ausschlussliste: {
    title: 'Ausschlussliste',
    description:
      'Kundinnen und Kunden ausschliessen (nach Domain, UID oder Firmenname): deren E-Mails bleiben sichtbar, erzeugen aber keinen Kunden. Getrennt von der internen AML-Blacklist.',
  },
};

/** Flat list of every nav item (order preserved). */
export const ADMIN_NAV_ITEMS: AdminNavItem[] = ADMIN_NAV.flatMap((g) => g.items);

/** All keys that make a page an "admin page" (System-Status's empty set excluded). */
export const ADMIN_ALL_KEYS: readonly string[] = [
  ...new Set(ADMIN_NAV_ITEMS.flatMap((i) => i.requiredKeys)),
];

/** Type guard: is `s` a known admin page key. */
export function isAdminPageKey(s: string | undefined): s is AdminPageKey {
  return s !== undefined && ADMIN_NAV_ITEMS.some((i) => i.key === s);
}

/** The AppShell entry shows only when the caller can reach ≥1 KEYED admin page. */
export function hasAnyAdminKey(access: Pick<EffectivePermissions, 'canAny'>): boolean {
  return access.canAny(ADMIN_ALL_KEYS);
}

/** The first nav item the caller may see (for the `/admin` index redirect). */
export function firstVisibleSection(
  access: Pick<EffectivePermissions, 'canAny'>,
): AdminPageKey | null {
  const item = ADMIN_NAV_ITEMS.find((i) => access.canAny(i.requiredKeys));
  return item?.key ?? null;
}

/** Required keys for one page key (empty for unknown/ungated). */
export function requiredKeysFor(key: AdminPageKey): readonly string[] {
  return ADMIN_NAV_ITEMS.find((i) => i.key === key)?.requiredKeys ?? [];
}
